// IMG TITLE ANNOTATIONS — Tier 1, purely deterministic DISPLAY layer (owner + Sol ratified, 2026-08-08).
//
// WHY: many users are IMGs whose role titles (General Practitioner, Medical Officer, House Officer,
// Registrar, …) read differently to a Canadian reviewer than at home. This maps an ALLOWLIST of title
// patterns to ONE fixed informational sentence each — an explanation of how the title READS, never a
// suggestion to rename (Canadian "family physician" implies certification the user may not hold; a
// retitle nudge would push misrepresentation).
//
// COPY CONTRACT (non-negotiable — every sentence below was calibrated against it):
//   - plain language; never name a framework;
//   - never suggest changing the title;
//   - never assert credential equivalence;
//   - never infer independence, seniority, or patient-care involvement beyond what the sentence states;
//   - every line opens with the consistent lead-in "How this reads to a Canadian reviewer:".
//
// MATCHING RULES: case-insensitive, word-boundary, against the entry TITLE only (never bullets, never
// the organization), allowlist-only, NO fuzzy matching. FIRST MATCH WINS, so ordering is load-bearing
// and explicit: "medical officer of health" precedes "medical officer"; "senior house officer" / "SHO"
// precede "house officer"; "senior registrar" precedes "registrar". A title matching nothing gets
// NOTHING — no fallback line, no inference.

export interface TitleAnnotationRule {
  /** Stable id for tests + debugging. */
  id: string;
  /** Word-boundary, case-insensitive patterns. Any match fires the rule. */
  patterns: RegExp[];
  /** The full ratified sentence, lead-in included. Verbatim — do not paraphrase. */
  note: string;
  /**
   * DEDUP (owner + Sol ratified, 2026-08-08): the rule's own pattern family, in generic wording, used
   * ONLY to compose the fixed dedup suffix "This applies to your other <family> roles below as well."
   * Never per-CV composition; never rendered on its own.
   */
  family: string;
  /**
   * EXCEPTION (Sol-ratified, high-risk distinction): a `repeats` rule fires its full note on EVERY
   * matching entry in a surface — never deduped. Only the observership rule carries it: an
   * observational-experience read guards against over-claiming patient care, so it must sit under
   * every matching title, every time.
   */
  repeats?: boolean;
}

// ORDER IS THE SPECIFICITY CONTRACT. Most-specific first; tested explicitly in titleAnnotations.test.ts.
export const TITLE_ANNOTATION_RULES: TitleAnnotationRule[] = [
  {
    id: 'medical-officer-of-health', // MUST precede the generic medical-officer rule
    patterns: [/\bmedical officer of health\b/i],
    note: 'How this reads to a Canadian reviewer: in Canada this exact title is a senior public-health appointment — a reviewer may read it that way, so your bullets carry the weight of showing what the role actually involved.',
    family: 'Medical Officer of Health',
  },
  {
    id: 'senior-house-officer', // MUST precede the house-officer rule ("senior house officer" contains it)
    patterns: [/\bsenior house officer\b/i, /\bSHO\b/i],
    note: 'How this reads to a Canadian reviewer: an early postgraduate training post; the abbreviation isn’t used in Canada, so consider that a reviewer may not know it.',
    family: 'Senior House Officer',
  },
  {
    id: 'house-officer',
    patterns: [/\bhouse officer\b/i, /\bhousemanship\b/i, /\bhouseman\b/i],
    note: 'How this reads to a Canadian reviewer: a supervised provisional training year, similar in spirit to a Canadian internship.',
    family: 'House Officer',
  },
  {
    id: 'senior-registrar', // MUST precede the registrar rule
    patterns: [/\bsenior registrar\b/i],
    note: 'How this reads to a Canadian reviewer: a senior residency-training post — not an administrative role, though the word ‘registrar’ can momentarily mislead a Canadian reader.',
    family: 'Senior Registrar',
  },
  {
    id: 'registrar',
    patterns: [/\bregistrar\b/i],
    note: 'How this reads to a Canadian reviewer: a residency-training post — not an administrative role, though the word ‘registrar’ can momentarily mislead a Canadian reader.',
    family: 'Registrar',
  },
  {
    id: 'general-practitioner',
    patterns: [/\bgeneral practitioner\b/i, /\bGP\b/i],
    note: 'How this reads to a Canadian reviewer: primary-care practice — the nearest Canadian occupational analogue is family practice. This is only how the title reads; your title itself is exactly right as it is.',
    family: 'General Practitioner',
  },
  {
    id: 'medical-officer', // AFTER medical-officer-of-health; also covers "resident medical officer"
    patterns: [/\bmedical officer\b/i],
    note: 'How this reads to a Canadian reviewer: a reviewer can’t infer this role’s scope from the title alone, so your bullets carry the weight of showing what it involved.',
    family: 'Medical Officer',
  },
  {
    id: 'consultant',
    patterns: [/\bconsultant\b/i],
    note: 'How this reads to a Canadian reviewer: a senior specialist appointment. That is a strong reading — your bullets should show the scope that supports it.',
    family: 'Consultant',
  },
  {
    id: 'nysc',
    patterns: [/\bNYSC\b/i, /\bnational youth service\b/i],
    note: 'How this reads to a Canadian reviewer: a national service placement — Canadian readers won’t know the program, so your bullets carry the weight of showing the clinical content.',
    family: 'national service',
  },
  {
    id: 'observership',
    patterns: [/\bobservership\b/i, /\bobserver\b/i, /\bclinical attachment\b/i, /\bshadowing\b/i],
    note: 'How this reads to a Canadian reviewer: an observational experience without direct patient care — Canadian reviewers value it for exposure, and read any patient-care claim under it carefully.',
    family: 'observership',
    repeats: true, // Sol-ratified exception — repeats on EVERY match, never deduped
  },
];

/**
 * The one entry point: the entry TITLE in → the ratified sentence out, or null.
 * Null means render NOTHING (allowlist-only; no fallback copy is ever invented).
 */
export function annotateTitle(title: string | null | undefined): string | null {
  return matchTitleRule(title)?.note ?? null;
}

/** FIRST MATCH WINS against the ordered allowlist; null when nothing matches. */
function matchTitleRule(title: string | null | undefined): TitleAnnotationRule | null {
  const t = (title ?? '').trim();
  if (!t) return null;
  for (const rule of TITLE_ANNOTATION_RULES) {
    if (rule.patterns.some((p) => p.test(t))) return rule;
  }
  return null;
}

/**
 * SURFACE-SCOPED DEDUP (owner + Sol ratified, 2026-08-08). Input: one rendered surface's entry titles
 * in DOCUMENT ORDER. Output: the per-index note to render (null = render nothing).
 *
 * Within the surface, each rule fires its full ratified note only on the FIRST matching entry; when
 * later same-rule matches exist, that first note gains the fixed generic suffix
 * "This applies to your other <family> roles below as well." — composed from the rule's own pattern
 * family, never per-CV composition. Later same-rule occurrences render nothing.
 *
 * EXCEPTION: a `repeats` rule (observership only) fires on EVERY match — never deduped.
 *
 * Pure + deterministic: dedup scope is exactly ONE call (each surface calls with its own list, so
 * L2 and the drawer are independent, and any data change recomputes from scratch).
 */
export function annotateTitles(titles: ReadonlyArray<string | null | undefined>): (string | null)[] {
  const matched = titles.map((t) => matchTitleRule(t));
  const total = new Map<string, number>();
  for (const r of matched) if (r && !r.repeats) total.set(r.id, (total.get(r.id) ?? 0) + 1);
  const seen = new Set<string>();
  return matched.map((r) => {
    if (!r) return null;
    if (r.repeats) return r.note;
    if (seen.has(r.id)) return null; // a later same-rule occurrence: render nothing
    seen.add(r.id);
    return (total.get(r.id) ?? 0) > 1
      ? `${r.note} This applies to your other ${r.family} roles below as well.`
      : r.note;
  });
}
